import { Response } from '../types/response';
import { PriceData, CoinGeckoAssetData } from '../../types';
import { CacheManager } from './cache-manager';
import { CacheConfig, CacheRefreshCallback } from './cache-types';
import { createCacheStorage } from './cache-storage';

/**
 * Configuration for different types of API cache
 */
export const API_CACHE_CONFIGS = {
  prices: {
    ttl: 20 * 60 * 1000, // 20 minutes
    refreshThreshold: 5 * 60 * 1000, // Refresh when 5 minutes left
    refreshInterval: 5 * 60 * 1000, // Check every 5 minutes
  } as CacheConfig,
  
  assetDetails: {
    ttl: 60 * 60 * 1000, // 1 hour
    refreshThreshold: 30 * 60 * 1000, // Refresh when 30 minutes left
    refreshInterval: 30 * 60 * 1000, // Check every 30 minutes
  } as CacheConfig,
};

/**
 * Price cache manager
 */
export class PriceCache {
  private cacheManager: CacheManager<number>;
  
  constructor(refreshCallback?: CacheRefreshCallback<number>) {
    const storage = createCacheStorage('price-cache');
    this.cacheManager = new CacheManager(
      storage,
      API_CACHE_CONFIGS.prices,
      refreshCallback
    );
  }

  /**
   * Get price for a single asset
   */
  async getPrice(assetId: string): Promise<number | null> {
    return await this.cacheManager.get(assetId);
  }

  /**
   * Get prices for multiple assets
   */
  async getPrices(assetIds: string[]): Promise<PriceData> {
    const cachedPrices = await this.cacheManager.getMultiple(assetIds);
    const priceData: PriceData = {};
    
    for (const assetId of assetIds) {
      if (cachedPrices[assetId] !== undefined) {
        priceData[assetId] = cachedPrices[assetId];
      }
    }
    
    return priceData;
  }

  /**
   * Set price for a single asset
   */
  async setPrice(assetId: string, price: number): Promise<Response<void>> {
    return await this.cacheManager.set(assetId, price);
  }

  /**
   * Set prices for multiple assets
   */
  async setPrices(priceData: PriceData): Promise<Response<void>> {
    return await this.cacheManager.setMultiple(priceData);
  }

  /**
   * Get missing asset IDs (not in cache or expired)
   */
  async getMissingAssetIds(assetIds: string[]): Promise<string[]> {
    const cachedPrices = await this.cacheManager.getMultiple(assetIds, { skipRefresh: true });
    return assetIds.filter(id => cachedPrices[id] === undefined);
  }

  /**
   * Force refresh prices for specific assets
   */
  async refreshPrices(assetIds: string[]): Promise<Response<void>> {
    return await this.cacheManager.refresh(assetIds);
  }

  /**
   * Clear all cached prices
   */
  async clear(): Promise<Response<void>> {
    return await this.cacheManager.clear();
  }

  /**
   * Get cache statistics
   */
  async getStats() {
    return await this.cacheManager.getStats();
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.cacheManager.destroy();
  }
}

/**
 * Asset details cache manager
 */
export class AssetDetailsCache {
  private cacheManager: CacheManager<CoinGeckoAssetData>;
  
  constructor(refreshCallback?: CacheRefreshCallback<CoinGeckoAssetData>) {
    const storage = createCacheStorage('asset-details-cache');
    this.cacheManager = new CacheManager(
      storage,
      API_CACHE_CONFIGS.assetDetails,
      refreshCallback
    );
  }

  /**
   * Get asset details for a single asset
   */
  async getAssetDetails(assetId: string): Promise<CoinGeckoAssetData | null> {
    return await this.cacheManager.get(assetId);
  }

  /**
   * Get asset details for multiple assets
   */
  async getMultipleAssetDetails(assetIds: string[]): Promise<Record<string, CoinGeckoAssetData>> {
    return await this.cacheManager.getMultiple(assetIds);
  }

  /**
   * Set asset details for a single asset
   */
  async setAssetDetails(assetId: string, details: CoinGeckoAssetData): Promise<Response<void>> {
    return await this.cacheManager.set(assetId, details);
  }

  /**
   * Set asset details for multiple assets
   */
  async setMultipleAssetDetails(detailsData: Record<string, CoinGeckoAssetData>): Promise<Response<void>> {
    return await this.cacheManager.setMultiple(detailsData);
  }

  /**
   * Get missing asset IDs (not in cache or expired)
   */
  async getMissingAssetIds(assetIds: string[]): Promise<string[]> {
    const cachedDetails = await this.cacheManager.getMultiple(assetIds, { skipRefresh: true });
    return assetIds.filter(id => cachedDetails[id] === undefined);
  }

  /**
   * Force refresh asset details for specific assets
   */
  async refreshAssetDetails(assetIds: string[]): Promise<Response<void>> {
    return await this.cacheManager.refresh(assetIds);
  }

  /**
   * Clear all cached asset details
   */
  async clear(): Promise<Response<void>> {
    return await this.cacheManager.clear();
  }

  /**
   * Get cache statistics
   */
  async getStats() {
    return await this.cacheManager.getStats();
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.cacheManager.destroy();
  }
}

/**
 * Combined API cache manager that handles both prices and asset details
 */
export class ApiCache {
  public priceCache: PriceCache;
  public assetDetailsCache: AssetDetailsCache;

  constructor(
    priceRefreshCallback?: CacheRefreshCallback<number>,
    assetDetailsRefreshCallback?: CacheRefreshCallback<CoinGeckoAssetData>
  ) {
    this.priceCache = new PriceCache(priceRefreshCallback);
    this.assetDetailsCache = new AssetDetailsCache(assetDetailsRefreshCallback);
  }

  /**
   * Get combined cache statistics
   */
  async getStats() {
    const [priceStats, assetStats] = await Promise.all([
      this.priceCache.getStats(),
      this.assetDetailsCache.getStats()
    ]);

    return {
      prices: priceStats,
      assetDetails: assetStats,
      total: {
        totalEntries: priceStats.totalEntries + assetStats.totalEntries,
        expiredEntries: priceStats.expiredEntries + assetStats.expiredEntries,
        validEntries: priceStats.validEntries + assetStats.validEntries,
        refreshableEntries: priceStats.refreshableEntries + assetStats.refreshableEntries,
      }
    };
  }

  /**
   * Clear all caches
   */
  async clearAll(): Promise<Response<void>> {
    const [priceResult, assetResult] = await Promise.all([
      this.priceCache.clear(),
      this.assetDetailsCache.clear()
    ]);

    if (priceResult instanceof Error) {
      return priceResult;
    }
    if (assetResult instanceof Error) {
      return assetResult;
    }

    return undefined;
  }

  /**
   * Cleanup all resources
   */
  destroy(): void {
    this.priceCache.destroy();
    this.assetDetailsCache.destroy();
  }
}
